import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { SubmissionStatus } from "@coaching/types";
import { watchInbox, type Submission } from "../api";
import { useCoach } from "../auth";
import { EmptyState, ErrorState, Centred } from "../components/States";
import { SubmissionCard, useRespond } from "../components/SubmissionCard";

export type Filter = "needs_action" | "in_progress" | "returned" | "closed" | "all";
const FILTERS: { key: Filter; label: string; statuses: SubmissionStatus[] | null }[] = [
    { key: "needs_action", label: "Needs your answer", statuses: ["submitted"] },
    { key: "in_progress", label: "In progress", statuses: ["accepted", "in_review"] },
    { key: "returned", label: "Returned", statuses: ["returned"] },
    { key: "closed", label: "Closed", statuses: ["declined", "expired", "cancelled"] },
    { key: "all", label: "All", statuses: null },
];
const isFilter = (v: string | null): v is Filter => FILTERS.some((f) => f.key === v);

export default function Inbox() {
    const { user, clubs } = useCoach();
    const [params, setParams] = useSearchParams();
    const [rows, setRows] = useState<Submission[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [clubId, setClubId] = useState<string>("");
    const [attempt, setAttempt] = useState(0);
    const { busyId, accepting, actionError, dialog, accept, decline } = useRespond();
    const filterParam = params.get("filter");
    const filter: Filter = isFilter(filterParam) ? filterParam : "needs_action";
    const setFilter = (f: Filter) => setParams(f === "needs_action" ? {} : { filter: f }, { replace: true });

    useEffect(() => {
        setError(null);
        return watchInbox(user.uid, setRows, (e) => setError(e.message));
    }, [user.uid, attempt]);

    const counts = useMemo(() => {
        const c: Record<Filter, number> = { needs_action: 0, in_progress: 0, returned: 0, closed: 0, all: 0 };
        for (const r of rows ?? []) {
            if (clubId && r.clubId !== clubId) continue;
            c.all++;
            for (const f of FILTERS) if (f.statuses?.includes(r.status)) c[f.key]++;
        }
        return c;
    }, [rows, clubId]);

    const visible = useMemo(() => {
        const f = FILTERS.find((x) => x.key === filter);
        return (rows ?? []).filter((r) => (!clubId || r.clubId === clubId) && (!f?.statuses || f.statuses.includes(r.status)));
    }, [rows, filter, clubId]);

    if (error) return <ErrorState title="Could not load your inbox" body={error} retry={() => setAttempt((n) => n + 1)} />;
    if (rows === null) return <Centred spinner>Loading your inbox</Centred>;

    return (
        <div className="inbox">
            <div className="inbox-head">
                <h1>Inbox</h1>
                {clubs.length > 1 && (
                    <select value={clubId} onChange={(e) => setClubId(e.target.value)} aria-label="Filter by club">
                        <option value="">All clubs</option>
                        {clubs.map((c) => <option key={c.clubId} value={c.clubId}>{c.clubName}</option>)}
                    </select>
                )}
            </div>
            <div className="chips" role="tablist">
                {FILTERS.map((f) => (
                    <button key={f.key} role="tab" aria-selected={filter === f.key} className={`chip ${filter === f.key ? "on" : ""}`} onClick={() => setFilter(f.key)}>
                        {f.label} <span className="count">{counts[f.key]}</span>
                    </button>
                ))}
            </div>
            {actionError && <p className="error-text" role="alert">{actionError}</p>}
            {visible.length === 0 ? (
                <EmptyState
                    title={rows.length === 0 ? "No footage yet" : "Nothing here"}
                    body={rows.length === 0
                        ? "When a player at your club sends you footage it will appear here and you will get a notification."
                        : "Try another filter."}
                />
            ) : (
                <ul className="cards">
                    {visible.map((s) => <SubmissionCard key={s.id} s={s} busy={busyId === s.id} accepting={accepting === s.id} onAccept={() => accept(s)} onDecline={() => decline(s)} />)}
                </ul>
            )}
            {dialog}
        </div>
    );
}
